import * as React from "react";
import { getModelContext, registerTools } from "./model-context";
import type { Data } from "../forms/form-data";
import type { JobsiteForms } from './tools';
import type { JobsiteRuntimeConfig } from '../types/runtime-config';
import { buildJobsiteWebMcpTools } from './tools';
import { fetchBookingPublic } from "../components/booking-widget/booking-api";
import { getWindowJobsiteConfig } from '../types/runtime-config';

const FORM_TIMEOUT_MS = 3000,

  /** The forms this site renders, so tools describe the fields this business
   *  actually asks for. Best-effort: a failure costs the shape, not the toolset. */
  resolveJobsiteForms = async (config: JobsiteRuntimeConfig): Promise<JobsiteForms> => {
    const { apiUrl, siteId } = config,
      slug = config.business?.slug,

      contact = apiUrl && siteId
        ? fetch(`${apiUrl}/api/sites/${siteId}/forms/contact`)
            .then((res) => {
              if (res.ok) {
                return res.json() as Promise<{ data?: Data | null }>;
              }
              return null;
            })
            .then((body) => body?.data ?? null)
            .catch(() => null)
        : Promise.resolve(null),

      booking = apiUrl && slug
        ? fetchBookingPublic(apiUrl, slug)
            .then((res) => {
              if (res.ok) {
                return res.data.formData;
              }
              return null;
            })
            .catch(() => null)
        : Promise.resolve(null),

      [contactForm, bookingForm] = await Promise.all([contact, booking]);
    return { booking: bookingForm, contact: contactForm };
  },

  /** Register this jobsite's WebMCP tools for the app's lifetime. Post-mount,
   *  because the tools read `window.__SERVICEPRO_CONFIG__`. */
  useJobsiteWebMcp = (): void => {
    React.useEffect(() => {
      const config = getWindowJobsiteConfig();
      // Offering a site's live booking tools inside its owner's editor is wrong.
      if (config.isEditing === true) {
        return;
      }

      // So is offering them from any frame: the dashboard embeds the live site as
      // A preview thumbnail, and an agent's booking belongs to a visitor's own
      // Session. A framed document still gets `modelContext` — `tools` is a
      // Policy-controlled feature allowlisted to `self`, so the calls are refused,
      // Not absent — which makes this a correctness guard, not an optimization.
      if (typeof window !== "undefined" && window.top !== window.self) {
        return;
      }

      // Before any fetch: with nothing to register on, a browser without WebMCP
      // — today, almost every browser — pays no request for this.
      if (!getModelContext()) {
        return;
      }

      let cancelled = false,
        unregister: (() => void) | undefined;
      // A stalled API costs the tools their form-specific shape, never the tools:
      // The read-only ones need no document at all.
      void Promise.race([
        resolveJobsiteForms(config),
        new Promise<JobsiteForms>((resolve) => setTimeout(() => {
          resolve({});
        }, FORM_TIMEOUT_MS)),
      ]).then((forms) => {
        if (cancelled) {
          return;
        }
        unregister = registerTools(buildJobsiteWebMcpTools(forms));
      });

      return () => {
        cancelled = true;
        unregister?.();
      };
    }, []);
  };

export { useJobsiteWebMcp };
